/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable, Signal } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { AuthenticationResult } from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { CountriesService, QuotationsService } from 'src/app/api';
import { EndorsementService } from 'src/app/api/api/endorsement.service';
import { PhonePrefixesService } from 'src/app/api/api/phonePrefixes.service';
import { CombinedStore } from 'src/app/core/combined.store';
import { environment } from 'src/environments/environment';
import { MSALInstanceFactory } from 'src/msal-config';
import { CANAL_VENTE_C } from '../constants/context.constants';
import {
  CONTRACT_VALIDATION_PATH,
  CROSS_SELLING_C_PATH,
  QUOTE_CONFIRMATION_PATH,
  SPECIAL_CONDITIONS_PATH,
  START_PATH,
  SUBSCRIPTION_PATH,
} from '../constants/route.constants';
import { MSAL_TOKEN } from '../constants/sessionstorage.constants';
import { PRODUCT_PARAM } from '../constants/url.constants';
import { isMetropolePathType, PathType } from '../enum/path-type.enum';
import { isPartner } from '../utils/brand-utils';
import {
  dispatchDeclaration,
  dispatchDeclarationFromContext,
  dispatchDevis,
  dispatchDevis_C,
  dispatchEndorsementOverview,
  dispatchOffer,
  dispatchOptions,
  dispatchPath,
  dispatchPayment,
  dispatchPolicy,
  dispatchSubscriber,
  dispatchSubscriber_C,
  processEndorsementOverview,
} from '../utils/dispatch-utils';
import { PoliciesService } from './../../api/api/policies.service';
import { Avenant } from 'src/app/api/model/avenant';

const CONTEXT = 'context';

@Injectable({
  providedIn: 'root',
})
export class RedirectGuard implements CanActivate {
  private store = inject(CombinedStore);
  contextId = '';
  contextData: any;
  pathType: PathType = PathType.UNDEFINED;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private quotationsService: QuotationsService,
    private policiesService: PoliciesService,
    private countriesService: CountriesService,
    private phonePrefixesService: PhonePrefixesService,
    private endorsementService: EndorsementService,
  ) {
    if (this.router.url.includes(START_PATH)) {
      const contextId = this.route.snapshot.paramMap.get(CONTEXT);
      const productCode = this.route.snapshot.paramMap.get(PRODUCT_PARAM)?.toUpperCase() || '';
      if (contextId !== null && contextId !== '') {
        this.contextId = contextId;
        // check for nested route for new backend context
        if (productCode == null || productCode === '') {
          this.contextData = this.store.context();
          return;
        } else {
          console.warn('Failed to retrieve context data from localStorage');
        }
      } else {
        if (isMetropolePathType(productCode)) {
          this.pathType = productCode;
          return;
        } else {
          console.warn('Invalid product code', productCode);
        }
      }
    }
  }

  async canActivate(
    route: ActivatedRouteSnapshot,
    // state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    const context = (route.paramMap.get(CONTEXT) ?? route.queryParams[CONTEXT]) as string;
    const product = route.queryParams[PRODUCT_PARAM];
    const pathType = isMetropolePathType(product) ? product : PathType.ESSENTIELLE;

    // Mise à jour du contexte ou du type de chemin
    if (context) {
      this.store.updateTokenContext(context);
    } else if (!this.store.path() || this.store.path() === PathType.UNDEFINED) {
      this.store.updatePath(pathType);
    }

    // Initialisation MSAL si nécessaire
    if (this.store.context().canalVente === CANAL_VENTE_C && !isPartner(environment.brand)) {
      await this.initializeMsal();
    }

    const avenants = this.store.context().avenants ?? [];
    if (avenants.length > 0) {
      return await this.handleEndorsementRedirect(avenants);
    }

    if (this.store.context().referenceContrat) {
      try {
        const summaryData = await firstValueFrom(
          this.policiesService.apiPoliciesPolicyReferenceOverviewGet(
            this.store.context().referenceContrat ?? '',
            this.store.context().token ?? '',
            this.store.context().souscripteur?.email ?? '',
          ),
        );

        // Dispatch des données si la requête réussit
        dispatchPolicy(this.store, summaryData);
        dispatchPayment(this.store, summaryData);
        dispatchSubscriber_C(this.store, summaryData);
        dispatchDevis_C(this.store, summaryData);

        //Toujours à false. Récupérer du backend.
        if (!!this.store.contract().EstSigne) {
          return this.router.createUrlTree([SUBSCRIPTION_PATH, CONTRACT_VALIDATION_PATH]);
        } else if (this.isQuoteResumedByPartnerAdvisor()) {
          return this.router.createUrlTree([SUBSCRIPTION_PATH, CROSS_SELLING_C_PATH]);
        } else {
          return this.router.createUrlTree([SUBSCRIPTION_PATH, SPECIAL_CONDITIONS_PATH]);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du contrat:', error);
        return false;
      }
    } // Vérification de la référence du devis
    else if (this.store.context().referenceDevis) {
      try {
        const summaryData = await firstValueFrom(
          this.quotationsService.apiQuotationsQuotationReferenceSummaryGet(
            this.store.context().referenceDevis ?? '',
            this.store.context().token ?? '',
            this.store.context().souscripteur?.email ?? '',
          ),
        );

        // Dispatch des données si la requête réussit
        dispatchDeclaration(this.store, summaryData);
        dispatchPath(this.store, summaryData);
        dispatchDevis(this.store, summaryData);
        dispatchOffer(this.store, summaryData);
        dispatchOptions(this.store, summaryData);

        await dispatchSubscriber(this.store, this.countriesService, this.phonePrefixesService, summaryData);

        // Re-apply context declaration on top of quotation summary data.
        // Les champs housingType et apartmentFloor viennent maintenant
        // directement du modèle MrhSubscriptionInformationModel.
        // La franchise est portée par le devis (authoritative) et ne doit pas
        // être écrasée par la valeur par défaut du contexte back-office.
        const contextDeclaration = this.store.context().declaration;
        if (contextDeclaration) {
          const deductibleTypeFromDevis = this.store.declaration().deductibleType;
          dispatchDeclarationFromContext(this.store, contextDeclaration);
          this.store.updateDeclaration({ deductibleType: deductibleTypeFromDevis });
        }

        // Redirection vers la confirmation du devis
        this.router.navigate([SUBSCRIPTION_PATH, QUOTE_CONFIRMATION_PATH]);
        return true;
      } catch (error) {
        console.error('Erreur lors de la récupération du devis:', error);
        return false;
      }
    } else {
      // Redirection vers le chemin de démarrage si aucune référence de devis
      await this.router.navigate([START_PATH]);
      return false; // Empêche l'accès à la route en cours
    }
  }

  private async initializeMsal() {
    const msalInstance = MSALInstanceFactory();
    try {
      await msalInstance.initialize();
      const response = await msalInstance.handleRedirectPromise();
      if (!response) {
        await msalInstance.loginPopup({ scopes: environment.apiConfig.scopes });
      }

      // Attempt to acquire token silently
      const account = msalInstance.getAllAccounts()[0];
      if (account) {
        try {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: environment.apiConfig.scopes,
            account: account,
          });

          this.saveMsalToken(tokenResponse);
          if (tokenResponse.account.name) {
            this.store.updateDevisClientAdvisorFullName(tokenResponse.account.name);
            this.store.updateContractClientAdvisorFullName(tokenResponse.account.name);
          }
        } catch (silentError) {
          console.error('Silent token acquisition failed, falling back to interactive method', silentError);

          // Fallback to interactive token acquisition
          const tokenResponse = await msalInstance.acquireTokenPopup({
            scopes: environment.apiConfig.scopes,
          });
          this.saveMsalToken(tokenResponse);
          if (tokenResponse.account.name) {
            this.store.updateDevisClientAdvisorFullName(tokenResponse.account.name);
            this.store.updateContractClientAdvisorFullName(tokenResponse.account.name);
          }
        }
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  }

  private saveMsalToken(tokenResponse: AuthenticationResult) {
    sessionStorage.setItem(MSAL_TOKEN, JSON.stringify(tokenResponse.accessToken));
  }

  private isQuoteResumedByPartnerAdvisor(): boolean {
    return (
      isPartner(environment.brand) &&
      this.store.context().codeOperation === 'REPRISE_DEVIS' &&
      this.store.context().modeConnexion === 'APP'
    );
  }

  private async handleEndorsementRedirect(avenants: Avenant[]){
    try {
      const overviews = await processEndorsementOverview(
        this.endorsementService,
        this.store.context().token as string,
        this.store.context().souscripteur?.email as string,
        this.store.contract().EstSigne || false,
        avenants,
      );

      dispatchEndorsementOverview(this.store, overviews.flat()[0], avenants);

      if (!!this.store.contract().EstSigne) {
        this.router.navigate([SUBSCRIPTION_PATH, CONTRACT_VALIDATION_PATH]);
      } else {
        this.router.navigate([SUBSCRIPTION_PATH, SPECIAL_CONDITIONS_PATH]);
      }
      return true;
    } catch (error) {
      console.error("Erreur lors de la récupération de l'avenant:", error);
      return false;
    }
  }
}
